import { getApiBaseUrl } from '@/lib/env';

export interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'OPTIONS' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  credentials?: RequestCredentials;
  responseType?: 'json' | 'blob';
}

class ApiClient {
  private baseURL: string | undefined;
  private isRedirectingToLogin: boolean = false;

  constructor(baseURL: string | undefined = undefined) {
    this.baseURL = baseURL;
  }

  private getBaseURL(): string {
    if (this.baseURL !== undefined) return this.baseURL;
    return getApiBaseUrl();
  }

  private isStaffContext(endpoint?: string): boolean {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
      return true;
    }
    if (endpoint && endpoint.startsWith('/admin')) {
      return true;
    }
    return false;
  }

  private getCookie(name: string): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : undefined;
  }

  private setCookie(name: string, value: string, days: number = 7): void {
    if (typeof document === 'undefined') return;
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${isSecure ? ';Secure' : ''}`;
  }

  private deleteCookie(name: string): void {
    if (typeof document === 'undefined') return;
    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const secureFlag = isSecure ? ';Secure' : '';
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax${secureFlag}`;
    document.cookie = `${name}=;max-age=0;path=/;SameSite=Lax${secureFlag}`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    document.cookie = `${name}=;max-age=0;path=/;`;
  }

  private getTenantId(endpoint?: string): string | undefined {
    if (typeof document === 'undefined') return undefined;
    // Check sessionStorage only for client impersonation sessions (NEVER on admin routes/endpoints)
    if (!this.isStaffContext(endpoint)) {
      const forceAuthUser = typeof window !== 'undefined' ? sessionStorage.getItem('force_auth_user') : null;
      if (forceAuthUser) {
        try {
          const parsedUser = JSON.parse(forceAuthUser);
          if (parsedUser.tenant?.id) return parsedUser.tenant.id;
        } catch {
          // fall through to cookie check
        }
      }
    }

    const authUser = this.getCookie('auth_user');
    if (!authUser) return undefined;
    try {
      const parsedUser = JSON.parse(decodeURIComponent(authUser));
      return parsedUser.tenant?.id;
    } catch {
      return undefined;
    }
  }

  private async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      credentials = 'include',
      responseType = 'json',
    } = config;

    // Check sessionStorage for force-login client token, but NEVER use it for staff/admin endpoints
    let token: string | undefined;
    if (typeof window !== 'undefined' && !this.isStaffContext(endpoint)) {
      token = sessionStorage.getItem('force_auth_token') || undefined;
    }
    if (!token) {
      token = this.getCookie('auth_token');
    }
    const tenantId = this.getTenantId(endpoint) || this.getCookie('tenant_id');
    const tenantSlug = this.getCookie('tenant_slug');

    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    const requestHeaders: Record<string, string> = {
      'ngrok-skip-browser-warning': 'true',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      ...headers,
    };

    if (!isFormData) {
      requestHeaders['Content-Type'] = 'application/json';
    }

    if (token) {
      requestHeaders.Authorization = `Bearer ${token}`;
    }

    if (tenantId) {
      requestHeaders['x-tenant-id'] = tenantId;
    }

    if (tenantSlug) {
      requestHeaders['x-tenant-slug'] = tenantSlug;
    }

    const requestConfig: RequestInit = {
      method,
      headers: requestHeaders,
      credentials,
      cache: 'no-store',
    };

    if (body && method !== 'GET' && method !== 'OPTIONS') {
      requestConfig.body = isFormData ? body : JSON.stringify(body);
    }

    try {
      let response: Response;
      const primaryUrl = `${this.getBaseURL()}${endpoint}`;
      try {
        response = await fetch(primaryUrl, requestConfig);
      } catch (fetchErr) {
        if (typeof window !== 'undefined' && this.getBaseURL().startsWith('http') && !endpoint.startsWith('/api')) {
          console.warn(`[ApiClient] Request to ${primaryUrl} failed (${(fetchErr as any)?.message}). Retrying via relative /api proxy...`);
          const fallbackUrl = `/api${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
          response = await fetch(fallbackUrl, requestConfig);
        } else {
          throw fetchErr;
        }
      }

      if (!response.ok) {
        if (response.status === 401) {
          if (typeof window !== 'undefined') {
            const isImpersonated = Boolean(sessionStorage.getItem('force_auth_token'));
            if (isImpersonated && !this.isStaffContext(endpoint)) {
              // Client impersonation session encountered 401: restore admin session
              sessionStorage.removeItem('force_auth_user');
              sessionStorage.removeItem('force_auth_token');
              sessionStorage.removeItem('force_auth_user_type');
              sessionStorage.removeItem('force_auth_session_id');
              sessionStorage.removeItem('force_auth_admin');
              sessionStorage.removeItem('force_auth_started_at');
              this.deleteCookie('force_auth');
              this.deleteCookie('client_access_gate');
              try {
                const { restoreAdminSessionAndReturn } = require('@/sdk/shared/auth/utils');
                restoreAdminSessionAndReturn('/admin/clients');
              } catch {
                window.location.href = '/admin/clients';
              }
              throw { status: 401, error: 'Unauthorized: session expired' };
            }

            const currentPath = window.location.pathname;
            if (!currentPath.includes('/login')) {
              if (!this.isRedirectingToLogin) {
                this.isRedirectingToLogin = true;

                const userType = this.getCookie('auth_user_type');
                const role = this.getCookie('client_role');
                const tenantSlug = this.getCookie('tenant_slug') || localStorage.getItem('last_tenant_slug');

                this.clearToken();
                this.deleteCookie('auth_user');
                this.deleteCookie('auth_user_type');
                sessionStorage.removeItem('force_auth_user');
                sessionStorage.removeItem('force_auth_token');
                sessionStorage.removeItem('force_auth_user_type');

                const isAdmin =
                  currentPath.startsWith('/admin') ||
                  userType === 'user' ||
                  role === 'admin' ||
                  role === 'super_admin';

                const targetLogin = isAdmin ? '/admin/login' : '/client/login';
                const searchParams = new URLSearchParams(window.location.search);
                if (tenantSlug && !searchParams.get('slug') && !searchParams.get('tenantSlug')) {
                  searchParams.set('slug', tenantSlug);
                }
                const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';

                window.location.href = `${targetLogin}${queryString}`;
              }
            }
          }
        }
        const error = await response.json().catch(() => ({ error: 'An error occurred' }));
        throw {
          status: response.status,
          ...error,
        };
      }

      if (responseType === 'blob') {
        return (await response.blob()) as unknown as T;
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body });
  }

  async put<T>(endpoint: string, body?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body });
  }

  async patch<T>(endpoint: string, body?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body });
  }

  async options<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'OPTIONS' });
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  setToken(token: string, role?: string): void {
    if (typeof window !== 'undefined' && !this.isStaffContext() && sessionStorage.getItem('force_auth_token')) {
      sessionStorage.setItem('force_auth_token', token);
      return;
    }
    this.setCookie('auth_token', token);
    if (role) {
      this.setCookie('client_role', role);
    }
  }

  getToken(): string | undefined {
    if (typeof window !== 'undefined' && !this.isStaffContext()) {
      const forceToken = sessionStorage.getItem('force_auth_token');
      if (forceToken) return forceToken;
    }
    return this.getCookie('auth_token');
  }

  getApiURL(): string {
    return this.getBaseURL();
  }

  clearToken(): void {
    if (typeof window !== 'undefined' && !this.isStaffContext() && sessionStorage.getItem('force_auth_token')) {
      sessionStorage.removeItem('force_auth_token');
      sessionStorage.removeItem('force_auth_user');
      sessionStorage.removeItem('force_auth_user_type');
      return;
    }
    this.deleteCookie('auth_token');
    this.deleteCookie('client_role');
  }
}

export const apiClient = new ApiClient();
export default ApiClient;
